import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const SettingsPanel = ({ isOpen, onClose, settings, onUpdateSettings }) => {
  const [localSettings, setLocalSettings] = useState(settings);

  const audioDevices = [
    { value: 'default', label: 'Default Microphone' },
    { value: 'device1', label: 'Built-in Microphone' },
    { value: 'device2', label: 'External USB Microphone' },
  ];

  const videoDevices = [
    { value: 'default', label: 'Default Camera' },
    { value: 'device1', label: 'Built-in Camera' },
    { value: 'device2', label: 'External Webcam' },
  ];

  const speakerDevices = [
    { value: 'default', label: 'Default Speakers' },
    { value: 'device1', label: 'Built-in Speakers' },
    { value: 'device2', label: 'Headphones' },
  ];

  const videoQualityOptions = [
    { value: 'auto', label: 'Auto (Recommended)' },
    { value: 'high', label: 'High (720p)' },
    { value: 'medium', label: 'Medium (480p)' },
    { value: 'low', label: 'Low (360p)' },
  ];

  const handleSave = () => {
    onUpdateSettings(localSettings);
    onClose();
  };

  const handleChange = (field, value) => {
    setLocalSettings((prev) => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <Icon name="Settings" size={24} className="text-primary" />
            <h2 className="text-xl font-semibold text-foreground">Session Settings</h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            onClick={onClose}
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Audio Settings */}
          <div>
            <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Mic" size={20} className="text-primary" />
              Audio Settings
            </h3>
            <div className="space-y-4">
              <Select
                label="Microphone"
                options={audioDevices}
                value={localSettings?.audioInput}
                onChange={(value) => handleChange('audioInput', value)}
              />
              <Select
                label="Speakers"
                options={speakerDevices}
                value={localSettings?.audioOutput}
                onChange={(value) => handleChange('audioOutput', value)}
              />
              <Checkbox
                label="Echo cancellation"
                description="Reduces echo and feedback during calls"
                checked={localSettings?.echoCancellation}
                onChange={(e) => handleChange('echoCancellation', e?.target?.checked)}
              />
              <Checkbox
                label="Noise suppression"
                description="Filters background noise for clearer audio"
                checked={localSettings?.noiseSuppression}
                onChange={(e) => handleChange('noiseSuppression', e?.target?.checked)}
              />
            </div>
          </div>

          {/* Video Settings */}
          <div className="pt-6 border-t border-border">
            <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Video" size={20} className="text-primary" />
              Video Settings
            </h3>
            <div className="space-y-4">
              <Select
                label="Camera"
                options={videoDevices}
                value={localSettings?.videoInput}
                onChange={(value) => handleChange('videoInput', value)}
              />
              <Select
                label="Video quality"
                description="Higher quality uses more bandwidth"
                options={videoQualityOptions}
                value={localSettings?.videoQuality}
                onChange={(value) => handleChange('videoQuality', value)}
              />
              <Checkbox
                label="Mirror my video"
                description="Show your video as others see it"
                checked={localSettings?.mirrorVideo}
                onChange={(e) => handleChange('mirrorVideo', e?.target?.checked)}
              />
            </div>
          </div>

          {/* Privacy Settings */}
          <div className="pt-6 border-t border-border">
            <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Lock" size={20} className="text-primary" />
              Privacy & Security
            </h3>
            <div className="space-y-4">
              <Checkbox
                label="Show connection quality indicator"
                checked={localSettings?.showConnectionQuality}
                onChange={(e) => handleChange('showConnectionQuality', e?.target?.checked)}
              />
              <Checkbox
                label="Enable chat backup"
                description="Save chat messages for post-session review"
                checked={localSettings?.chatBackup}
                onChange={(e) => handleChange('chatBackup', e?.target?.checked)}
              />
            </div>
          </div>

          {/* Accessibility */}
          <div className="pt-6 border-t border-border">
            <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Accessibility" size={20} className="text-primary" />
              Accessibility
            </h3>
            <div className="space-y-4">
              <Checkbox
                label="Enable captions (when available)"
                description="Display real-time transcription of audio"
                checked={localSettings?.captions}
                onChange={(e) => handleChange('captions', e?.target?.checked)}
              />
              <Checkbox
                label="High contrast mode"
                description="Increase visibility of controls"
                checked={localSettings?.highContrast}
                onChange={(e) => handleChange('highContrast', e?.target?.checked)}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;